import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ApiError,
  listRoles,
  resetRolePermissions,
  setRolePermissions,
  type RoleView,
} from '../api.js';
import { formatDate } from '../labels.js';

/**
 * What each role is allowed to do (ADR-035).
 *
 * THE SCREEN THIS IS
 * ------------------
 * One role at a time, and a grid of tick boxes beneath it. Not all ten roles
 * side by side: that is 31 resources × 7 actions × 10 roles on one page, which
 * is the version that looks impressive and that nobody can use. An admin comes
 * here to answer one question — "can the QS see costs?" — and leaves.
 *
 * THREE KINDS OF BOX, AND WHY TWO OF THEM ARE NOT TICKABLE
 * -------------------------------------------------------
 * The server refuses both of these anyway. They are drawn as unavailable, with
 * the reason beside them, because a screen that offers a tick the server then
 * refuses is worse than one that does not offer it: the admin ticks, saves,
 * sees an error they did not expect, and learns not to trust the screen.
 *
 *   locked     — Company Admin's own "roles" and "people" boxes. Untick those
 *                and nobody in the company can ever put them back.
 *   forbidden  — money, for clients and subcontractors. `04` §External user
 *                rules: enforced by role and not by a flag, so it is not a
 *                setting.
 *
 * NOTHING SAVES UNTIL YOU PRESS SAVE
 * ----------------------------------
 * Ticks are held locally and sent as one change for the whole role. Auto-saving
 * each box would put a dozen audit events against one decision and, worse,
 * would let a half-finished edit — view removed, edit not yet — be live for the
 * seconds in between. On this screen those seconds matter.
 */

interface Props {
  readonly canManage: boolean;
}

/** `04`'s letters, in its order, with the words an admin would use. */
const ACTION_LABELS: Readonly<Record<string, string>> = {
  view: 'See',
  create: 'Add',
  edit: 'Change',
  delete: 'Remove',
  approve: 'Approve',
  export: 'Export',
  admin: 'Administer',
};

/** The 31 resources in the language of the job, not the schema. */
const RESOURCE_LABELS: Readonly<Record<string, string>> = {
  company: 'Company details',
  user: 'People',
  role: 'Roles and permissions',
  template: 'Templates',
  project: 'Projects',
  stage: 'Project stages',
  milestone: 'Milestones',
  location: 'Locations',
  team: 'Project team',
  task: 'Tasks',
  deliverable: 'Drawings due',
  submittal: 'Submittals',
  material: 'Materials',
  supplier: 'Suppliers',
  delivery: 'Deliveries',
  activity: 'Site activities',
  diary: 'Site diary',
  photo: 'Photos',
  inspection: 'Inspections',
  ncr: 'NCRs',
  snag: 'Snags',
  handover: 'Handover',
  budget: 'Budget',
  cost: 'Costs',
  variation: 'Variations',
  billing: 'Invoices',
  document: 'Documents',
  report: 'Reports',
  pulse: 'Project health',
  audit: 'Audit trail',
  ai: 'AI assistant',
};

type Ticks = Readonly<Record<string, ReadonlySet<string>>>;

function ticksOf(role: RoleView): Ticks {
  const out: Record<string, Set<string>> = {};
  for (const row of role.resources) {
    const held = row.cells.filter((c) => c.held).map((c) => c.action);
    if (held.length > 0) out[row.resource] = new Set(held);
  }
  return out;
}

function sameTicks(a: Ticks, b: Ticks): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    const left = a[key] ?? new Set<string>();
    const right = b[key] ?? new Set<string>();
    if (left.size !== right.size) return false;
    for (const action of left) if (!right.has(action)) return false;
  }
  return true;
}

export function Roles({ canManage }: Props): JSX.Element {
  const [roles, setRoles] = useState<readonly RoleView[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [ticks, setTicks] = useState<Ticks>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await listRoles();
      setRoles(next);
      setSelected((current) => current ?? next[0]?.key ?? null);
    } catch (caught) {
      if (caught instanceof ApiError && (caught.status === 403 || caught.status === 404)) {
        setDenied(true);
        return;
      }
      setError(caught instanceof ApiError ? caught.message : 'Could not load the roles.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const role = useMemo(
    () => roles?.find((r) => r.key === selected) ?? null,
    [roles, selected],
  );

  // Reset the local ticks whenever the chosen role changes or the server
  // answers again. The server's copy is the truth; this screen only ever holds
  // a proposal.
  useEffect(() => {
    if (role) setTicks(ticksOf(role));
    setSaved(false);
  }, [role]);

  const dirty = role !== null && !sameTicks(ticks, ticksOf(role));

  if (denied) {
    return (
      <>
        <h3 className="sub-head">Roles</h3>
        <p className="hint">Your role does not include what other roles are allowed to do.</p>
      </>
    );
  }

  function toggle(resource: string, action: string): void {
    setSaved(false);
    setTicks((current) => {
      const next: Record<string, Set<string>> = {};
      for (const [key, set] of Object.entries(current)) next[key] = new Set(set);
      const bucket = (next[resource] ??= new Set());
      if (bucket.has(action)) bucket.delete(action);
      else bucket.add(action);
      if (bucket.size === 0) delete next[resource];
      return next;
    });
  }

  async function save(): Promise<void> {
    if (!role) return;
    setBusy(true);
    setError(null);
    try {
      setRoles(
        await setRolePermissions(
          role.key,
          Object.entries(ticks).map(([resource, actions]) => ({
            resource,
            actions: [...actions],
          })),
        ),
      );
      setSaved(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not save.');
    } finally {
      setBusy(false);
    }
  }

  async function reset(): Promise<void> {
    if (!role) return;
    setBusy(true);
    setError(null);
    try {
      setRoles(await resetRolePermissions(role.key));
      setSaved(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h3 className="sub-head">Roles</h3>

      <p className="hint">
        What each role may do. Everything starts from what the product ships with, and a role
        you have changed says so — you can always put it back.
      </p>

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {roles === null && !error && <p aria-live="polite">Loading…</p>}

      {roles && (
        <div className="role-picker">
          {roles.map((r) => (
            <button
              key={r.key}
              type="button"
              className={r.key === selected ? 'role-chip current' : 'role-chip'}
              onClick={() => setSelected(r.key)}
            >
              {r.name}
              {r.is_customised && (
                <span className="role-chip-mark" title="Changed from the default">
                  {' '}
                  ·
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {role && (
        <>
          <div className="role-head">
            <h4>{role.name}</h4>
            {role.is_customised ? (
              <p className="hint">
                Changed from what the product ships with
                {role.customised_by && ` by ${role.customised_by}`}
                {role.customised_at && ` on ${formatDate(role.customised_at)}`}.
              </p>
            ) : (
              <p className="hint">Exactly what the product ships with.</p>
            )}
          </div>

          {saved && !dirty && (
            <p className="hint" aria-live="polite">
              Saved. It applies to everybody on their next click — nobody needs to sign in again.
            </p>
          )}

          <div className="table-scroll">
            <table className="grid role-grid">
              <caption className="visually-hidden">
                What {role.name} is allowed to do, by area.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Area</th>
                  {role.resources[0]?.cells.map((cell) => (
                    <th key={cell.action} scope="col">
                      {ACTION_LABELS[cell.action] ?? cell.action}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {role.resources.map((row) => (
                  <tr key={row.resource}>
                    <th scope="row">{RESOURCE_LABELS[row.resource] ?? row.resource}</th>
                    {row.cells.map((cell) => {
                      const on = (ticks[row.resource] ?? new Set()).has(cell.action);
                      const label = `${ACTION_LABELS[cell.action] ?? cell.action} — ${
                        RESOURCE_LABELS[row.resource] ?? row.resource
                      }`;
                      if (cell.state === 'forbidden') {
                        return (
                          <td key={cell.action} className="role-cell role-cell-forbidden">
                            <span aria-label={`${label}: never allowed for this role`}>—</span>
                          </td>
                        );
                      }
                      return (
                        <td key={cell.action} className="role-cell">
                          <input
                            type="checkbox"
                            checked={on}
                            aria-label={
                              cell.state === 'locked'
                                ? `${label}: always on, and cannot be switched off`
                                : label
                            }
                            disabled={!canManage || busy || cell.state === 'locked'}
                            onChange={() => toggle(row.resource, cell.action)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="hint">
            A dash means never allowed for this role — a client or subcontractor can never be
            given money. A tick you cannot switch off keeps somebody able to change these
            settings back.
          </p>

          {canManage && (
            <div className="button-row">
              <button type="button" className="inline" disabled={!dirty || busy} onClick={() => void save()}>
                {busy ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="secondary inline"
                disabled={busy || !dirty}
                onClick={() => setTicks(ticksOf(role))}
              >
                Cancel
              </button>
              {role.is_customised && (
                <button
                  type="button"
                  className="link-button"
                  disabled={busy}
                  onClick={() => void reset()}
                >
                  Put back to the default
                </button>
              )}
            </div>
          )}

          {!canManage && (
            <p className="hint">Your role can see this, but not change it.</p>
          )}
        </>
      )}
    </>
  );
}
